import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import {
  Wallet, Plus, Trash2, Edit2, Receipt, TrendingUp, TrendingDown,
  Calendar, Building2, Search, Filter, Download, IndianRupee,
  Users, Home, Zap, ShoppingCart, Package, MoreHorizontal, Upload, Image, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = [
  { value: 'staff', label: 'Staff Salaries', icon: Users, color: 'bg-blue-100 text-blue-700' },
  { value: 'rent', label: 'Rent', icon: Home, color: 'bg-purple-100 text-purple-700' },
  { value: 'utilities', label: 'Utilities', icon: Zap, color: 'bg-yellow-100 text-yellow-700' },
  { value: 'purchases', label: 'Purchases', icon: ShoppingCart, color: 'bg-green-100 text-green-700' },
  { value: 'equipment', label: 'Equipment', icon: Package, color: 'bg-orange-100 text-orange-700' },
  { value: 'marketing', label: 'Marketing', icon: TrendingUp, color: 'bg-pink-100 text-pink-700' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'bg-slate-100 text-slate-700' }
];

export default function ExpenseTrackerPage() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return new Date().toISOString().slice(0, 7);
  });
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [useDateRange, setUseDateRange] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    category: 'other',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    description: '',
    vendor_name: '',
    receipt_image: null
  });

  const fetchData = async () => {
    try {
      let expUrl = `/expenses`;
      let sumUrl = `/expenses/summary`;
      
      if (useDateRange && dateRange.start && dateRange.end) {
        expUrl += `?start_date=${dateRange.start}&end_date=${dateRange.end}`;
        sumUrl += `?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      } else {
        expUrl += `?month=${selectedMonth}`;
        sumUrl += `?month=${selectedMonth}`;
      }
      
      if (categoryFilter !== 'all') {
        expUrl += `&category=${categoryFilter}`;
      }
      
      const [expRes, sumRes] = await Promise.all([
        API.get(expUrl),
        API.get(sumUrl)
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, categoryFilter, useDateRange, dateRange.start, dateRange.end]);

  const resetForm = () => {
    setFormData({
      category: 'other',
      amount: '',
      date: new Date().toISOString().slice(0, 10),
      description: '',
      vendor_name: '',
      receipt_image: null
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('category', formData.category);
    formDataToSend.append('amount', formData.amount);
    formDataToSend.append('date', formData.date);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('vendor_name', formData.vendor_name);
    if (formData.receipt_image) {
      formDataToSend.append('receipt_image', formData.receipt_image);
    }

    try {
      if (editingExpense) {
        await fetch(`${BACKEND_URL}/api/expenses/${editingExpense.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` },
          body: formDataToSend
        });
        toast.success('Expense updated');
      } else {
        await fetch(`${BACKEND_URL}/api/expenses`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` },
          body: formDataToSend
        });
        toast.success('Expense added');
      }
      setShowAddDialog(false);
      setEditingExpense(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await API.delete(`/expenses/${id}`);
      toast.success('Expense deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      amount: String(expense.amount),
      date: expense.date,
      description: expense.description || '',
      vendor_name: expense.vendor_name || '',
      receipt_image: null
    });
    setShowAddDialog(true);
  };

  const getCategoryInfo = (cat) => {
    return EXPENSE_CATEGORIES.find(c => c.value === cat) || EXPENSE_CATEGORIES[EXPENSE_CATEGORIES.length - 1];
  };

  // Zero-typing OCR: reads a snapped/uploaded bill photo and auto-fills vendor/date/amount
  const handleReceiptFile = async (file) => {
    if (!file) return;
    setFormData(prev => ({ ...prev, receipt_image: file }));
    if (!file.type.startsWith('image/')) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/expenses/ocr-extract`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` },
        body: fd
      });
      const data = await res.json();
      if (res.ok) {
        setFormData(prev => ({
          ...prev,
          vendor_name: data.vendor_name || prev.vendor_name,
          date: data.date || prev.date,
          amount: data.total_amount ? String(data.total_amount) : prev.amount,
        }));
        toast.success('Bill details auto-filled - review & confirm');
      } else {
        toast.error(data.detail || 'Could not read bill automatically, please fill manually');
      }
    } catch (err) {
      toast.error('Could not read bill automatically, please fill manually');
    }
    setOcrLoading(false);
  };

  // Quick date-filter presets
  const applyDatePreset = (preset) => {
    const fmt = d => d.toISOString().slice(0, 10);
    const today = new Date();
    let start = new Date(today), end = new Date(today);
    if (preset === 'yesterday') { start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); }
    else if (preset === 'this_month') { start = new Date(today.getFullYear(), today.getMonth(), 1); }
    else if (preset === 'last_30') { start.setDate(start.getDate() - 30); }
    setUseDateRange(true);
    setDateRange({ start: fmt(start), end: fmt(end) });
  };

  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = !searchQuery || 
      exp.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      exp.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="expense-tracker-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Expense Tracker</h1>
          <p className="text-slate-500 text-sm">Track clinic expenses and monitor profitability</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {!useDateRange ? (
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[180px]" data-testid="month-selector">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {generateMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 text-sm justify-start" data-testid="date-range-popover-trigger">
                  <Calendar className="w-4 h-4 mr-2" />
                  {dateRange.start ? `${dateRange.start} to ${dateRange.end}` : 'Pick date range'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('today')} data-testid="date-preset-today">Today</Button>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('yesterday')} data-testid="date-preset-yesterday">Yesterday</Button>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('this_month')} data-testid="date-preset-this-month">This Month</Button>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('last_30')} data-testid="date-preset-last-30">Last 30 Days</Button>
                </div>
                <CalendarWidget
                  mode="range"
                  selected={{ from: dateRange.start ? new Date(dateRange.start) : undefined, to: dateRange.end ? new Date(dateRange.end) : undefined }}
                  onSelect={(range) => {
                    const fmt = d => d.toISOString().slice(0, 10);
                    setDateRange({ start: range?.from ? fmt(range.from) : '', end: range?.to ? fmt(range.to) : (range?.from ? fmt(range.from) : '') });
                  }}
                  numberOfMonths={1}
                  data-testid="expense-date-range-calendar"
                />
              </PopoverContent>
            </Popover>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              setUseDateRange(!useDateRange);
              if (!useDateRange) {
                setDateRange({ start: '', end: '' });
              }
            }}
            data-testid="toggle-date-range"
          >
            {useDateRange ? 'Use Month' : 'Date Range'}
          </Button>
          <Button onClick={() => { resetForm(); setEditingExpense(null); setShowAddDialog(true); }} data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">₹{summary.total_revenue?.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600">₹{summary.total_expenses?.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`border-l-4 ${summary.net_profit >= 0 ? 'border-l-teal-500' : 'border-l-orange-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Net Profit</p>
                  <p className={`text-2xl font-bold ${summary.net_profit >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
                    {summary.net_profit >= 0 ? '+' : ''}₹{summary.net_profit?.toLocaleString()}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${summary.net_profit >= 0 ? 'bg-teal-100' : 'bg-orange-100'}`}>
                  <Wallet className={`w-6 h-6 ${summary.net_profit >= 0 ? 'text-teal-600' : 'text-orange-600'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Expense Count</p>
                  <p className="text-2xl font-bold text-blue-600">{summary.expense_count}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Receipt className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Breakdown */}
      {summary && Object.keys(summary.category_breakdown || {}).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Expense Breakdown by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {EXPENSE_CATEGORIES.map(cat => {
                const amount = summary.category_breakdown[cat.value] || 0;
                const percentage = summary.total_expenses > 0 ? (amount / summary.total_expenses * 100).toFixed(1) : 0;
                const Icon = cat.icon;
                return (
                  <div key={cat.value} className="p-3 rounded-lg border hover:shadow-sm transition-shadow cursor-pointer"
                    onClick={() => setCategoryFilter(categoryFilter === cat.value ? 'all' : cat.value)}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cat.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-medium text-slate-600">{cat.label}</span>
                    </div>
                    <p className="text-lg font-bold">₹{amount.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{percentage}%</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expenses List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>
              Expenses {categoryFilter !== 'all' && `- ${getCategoryInfo(categoryFilter).label}`}
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-[180px] h-9 text-sm"
                  data-testid="expense-search"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[140px] h-9 text-sm" data-testid="category-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No expenses found for this period</p>
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setEditingExpense(null); setShowAddDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Add First Expense
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredExpenses.map(expense => {
                const catInfo = getCategoryInfo(expense.category);
                const Icon = catInfo.icon;
                return (
                  <div key={expense.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border hover:bg-slate-50 transition-colors" data-testid={`expense-item-${expense.id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${catInfo.color}`}>
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{expense.description || catInfo.label}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <span>{expense.date}</span>
                          {expense.vendor_name && (
                            <>
                              <span>•</span>
                              <span className="truncate">{expense.vendor_name}</span>
                            </>
                          )}
                          {expense.source === 'auto' && (
                            <Badge variant="secondary" className="text-[10px]">Auto</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 pl-12 sm:pl-0">
                      <p className="font-bold text-base sm:text-lg">₹{expense.amount.toLocaleString()}</p>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEdit(expense)} data-testid={`edit-expense-${expense.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" onClick={() => handleDelete(expense.id)} data-testid={`delete-expense-${expense.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {editingExpense ? 'Edit Expense' : 'Add New Expense'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                Upload / Snap Bill (optional)
                {ocrLoading && <span className="text-teal-600 text-xs font-normal">Reading bill...</span>}
              </Label>
              <div
                className={`mt-1 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${dragActive ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'}`}
                onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={e => { e.preventDefault(); setDragActive(false); const f = e.dataTransfer.files?.[0]; if (f) handleReceiptFile(f); }}
                onClick={() => fileInputRef.current?.click()}
                data-testid="bill-dropzone"
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  capture="environment"
                  onChange={e => handleReceiptFile(e.target.files[0])}
                  className="hidden"
                  data-testid="bill-file-input"
                />
                {ocrLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" />
                ) : formData.receipt_image ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                    <Image className="w-4 h-4" /> {formData.receipt_image.name}
                    <Button type="button" variant="ghost" size="sm" className="h-6 px-1" onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, receipt_image: null })); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-slate-400">
                    <Upload className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs">Drag & drop or tap to snap a photo of the bill</p>
                    <p className="text-[10px] mt-0.5">Vendor, date & amount will auto-fill - just confirm</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Category *</Label>
              <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="mt-1" data-testid="expense-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4" />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (₹) *</Label>
                <Input 
                  type="number"
                  value={formData.amount}
                  onChange={e => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="expense-amount-input"
                  required
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1"
                  data-testid="expense-date-input"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input 
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Monthly staff salary, Electricity bill"
                className="mt-1"
                data-testid="expense-description-input"
              />
            </div>

            <div>
              <Label>Vendor/Payee Name</Label>
              <Input 
                value={formData.vendor_name}
                onChange={e => setFormData({ ...formData, vendor_name: e.target.value })}
                placeholder="e.g., BESCOM, Property Owner"
                className="mt-1"
                data-testid="expense-vendor-input"
              />
            </div>


            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); setEditingExpense(null); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-expense-btn">
                {editingExpense ? 'Update Expense' : 'Add Expense'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
